// Bind·Entropy: a displacement. Two loci are crossed over, so a cast naming one
// is served by the other.
//
// **The whole mechanism is a redirection at cast time**, and that is the reason
// this file is short. There is no settle system, no invariant to hold, no
// counterweight to clamp and nothing to bill the caster for -- because nothing
// is created or destroyed. `applyTransferEffect` resolves its two loci through
// `displacedEntropyLocus` and then does exactly what it always did to whatever
// comes back.
//
// **Conservation is structural rather than checked.** A Transfer moves a fixed
// amount between exactly two loci; the link only changes *which* two. There is
// no arithmetic anywhere that could produce Entropy from nowhere, so there is no
// exploit to find. That is the whole reason this replaced the two readings
// before it (2026-08-16):
//
//   - **Anti-correlation cannot conserve, ever.** `dS_B = -dS_C` holds the
//     pair's total fixed by definition, so anything arriving from outside has to
//     be annihilated and anything leaving has to be conjured. That was not a bug
//     in the implementation; it is what the rule means. It also left a real
//     pump: a caster at 0 warmth paid nothing for the conjuring direction.
//   - **Sharing conserves but fragments.** Two loci settling on their mean turns
//     one countable 4 into two 2s, and a 2 sits below every threshold the 4 was
//     going to cross -- so Entropy could be made to *vanish beneath* a rust or a
//     relic transition rather than being spent on it. Conserved and useless is
//     still a way for a puzzle to be bypassed.
//
// Displacement has neither problem: the quantity that arrives is the quantity
// that was sent, undivided, just somewhere else.
//
// **Both roles displace, which is what makes it symmetric.** A deposit named at
// one end lands on the other; a withdrawal named at one end is drawn from the
// other. So the two ends are each other's remote control, and an object's own
// Entropy is reachable only by naming its partner -- which is the mechanic
// rather than a side effect of it, and is what lets a puzzle put one end
// somewhere the caster can never face.
import { RoomDef, ObjectDef } from './types';

/** The far end of `obj`'s Entropy link, or undefined if it is not an end of one.
 *
 * Two ways to be an end, and they are checked in that order:
 *
 *   1. `obj` **carries** the cluster and names a partner.
 *   2. Something else carries one naming `obj`.
 *
 * The order is the tie-break for an object that is both -- B carrying a link to
 * C while D carries one to B. Carrying wins, deterministically, because that is
 * the link the player struck *at* this object. Nothing authored does this today;
 * the rule is here so that it is a decision rather than whatever `find` returned
 * first.
 *
 * A link naming the caster (`sourceId` absent) has no far end: the caster is not
 * an object and holds no Entropy. A link naming something that has left the room
 * has none either. Both read as "not an end", so the cast simply acts where it
 * was aimed. */
export function entropyPartner(room: RoomDef, obj: ObjectDef): ObjectDef | undefined {
  const own = obj.entropyBind?.sourceId;
  if (own && own !== obj.id) {
    const partner = room.objects.find((o) => o.id === own);
    if (partner) return partner;
  }
  return room.objects.find((o) => o.entropyBind?.sourceId === obj.id && o.id !== obj.id);
}

/** Where a cast naming `obj` actually lands: its partner if it is an end of a
 * link, otherwise itself.
 *
 * Called by `applyTransferEffect` for both of its loci, and **only for
 * Entropy** -- the link says nothing about any other concept, so a Transfer of
 * Energy at a linked object is untouched by it. */
export function displacedEntropyLocus(room: RoomDef, obj: ObjectDef | undefined): ObjectDef | undefined {
  if (!obj) return obj;
  return entropyPartner(room, obj) ?? obj;
}
